import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Button,
  ButtonModel,
  DrawerContext,
  IconSolidInfo,
  InputError,
  InteractiveArea,
  Text,
} from '@notino/react-styleguide';

import { CloseButton } from '@components/BottomSheet/styled';
import { CdnImage } from '@components/CdnImage';
import { Flex } from '@components/Flex';
import { commonMessages } from '@utils/commonMessages';

import { IActions } from '../context/EngravingTryItFirst/model';

import { messages } from './engravingMessages';
import {
  InputErrorWrapper,
  DrawerInput,
  ContentWrapper,
  EngravingImage,
  DrawerTitle,
  InfoBanner,
  H3,
  Footer,
  Wrapper,
  CharCounter,
} from './styled';
import { useEngravingContent } from './useEngravingContent';
import { resolveEngravingImageAttribute } from './utils';

interface IEngravingDrawerProps {
  imageAlt: string;
  code: string;
  maxChars: number;
  image?: string;
  engravingInitials: string;
  changeEngravingInitials: IActions['changeEngravingInitials'];
}

export const EngravingDrawer: React.FC<IEngravingDrawerProps> = ({
  imageAlt,
  image,
  maxChars,
  code,
  changeEngravingInitials,
  engravingInitials,
}) => {
  const { formatMessage } = useIntl();
  const { closeDrawer } = DrawerContext.useDrawerContext();
  const { handleInitialsChange, handleSave, initials, invalid } =
    useEngravingContent({
      onContentClose: closeDrawer,
      engravingInitials,
      changeEngravingInitials,
    });

  return (
    <Wrapper>
      <ContentWrapper>
        <Flex justify="space-between">
          <DrawerTitle>
            <FormattedMessage {...messages.drawerTitle} />
          </DrawerTitle>
          <InteractiveArea
            size="44px"
            onClick={closeDrawer}
            aria-label={formatMessage(commonMessages.close)}
          >
            <CloseButton />
          </InteractiveArea>
        </Flex>

        <EngravingImage>
          {image ? (
            <CdnImage src={image} alt={imageAlt} sourceKey="engraving" />
          ) : (
            <img {...resolveEngravingImageAttribute(code)} alt={imageAlt} />
          )}
        </EngravingImage>

        <InfoBanner>
          <IconSolidInfo color="icon.tertiary" height="16px" width="16px" />
          <Text typography="labelSmall400" color="text.secondary">
            <FormattedMessage {...messages.engravingPreview} />
          </Text>
        </InfoBanner>

        <H3>
          <FormattedMessage {...messages.engravingYourText} />
        </H3>
        <div>
          <CharCounter>
            {initials.length}/{maxChars}
          </CharCounter>
          <DrawerInput
            type="text"
            aria-label={formatMessage(messages.engravingYourText)}
            placeholder="LOVE"
            maxLength={maxChars}
            onChange={handleInitialsChange}
            value={initials}
            invalid={invalid}
            autoFocus={true}
            onKeyDown={(e) => e.key === 'Enter' && handleSave(e)}
          />

          {invalid && (
            <InputErrorWrapper>
              <InputError>
                <FormattedMessage {...messages.engravingInvalidCharacters} />
              </InputError>
            </InputErrorWrapper>
          )}
        </div>
      </ContentWrapper>
      <Footer>
        <Button
          onClick={handleSave}
          fullWidth={true}
          buttonStyle={ButtonModel.Styles.primary}
          disabled={invalid || !initials.trim()}
        >
          <FormattedMessage {...messages.addEngraving} />
        </Button>
      </Footer>
    </Wrapper>
  );
};
